import { Global } from '@emotion/react'
import {
  addDays,
  endOfMonth,
  format,
  formatISO,
  isSameDay,
  isSameMonth,
  setDate,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import PropTypes, { oneOf } from 'prop-types'
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useFeatureFlags } from '@opallabs/api'
import {
  ControlBar,
  DateNavigator,
  parseQuickfindHash,
  WorkspaceMonthCalendar,
} from '@opallabs/calendar'
import { Timescale } from '@opallabs/concepts'
import { GlobalGemColors } from '@opallabs/gem'
import { DefaultQueryClientProvider } from '@opallabs/higgs'
import {
  MessageKeys,
  ThemeProvider,
  useBackboneUserRole,
  useWorkspace,
} from '@opallabs/hooks'
import {
  kuiperClassNames,
  Conditional,
  navigateToContent,
  navigateToMoment,
} from '@opallabs/kuiper'
import { CurrentUserProvider } from '@opallabs/plasma'
import { AnalyticsConsumer } from '@opallabs/quanta'
import DayView from '../DayView'
import Header from '../Header'
import FilterButton from '../Header/FilterButton'
import {
  inMarketPreviewRoute,
  monthFilterPreviewParams,
} from '../Header/inMarketPreviewRoute'
import ConnectedHistogram from '../Histogram/ConnectedHistogram'
import { getAdjacentDateWithData } from '../Histogram/helpers'
import MomentDropToast from '../MomentDropToast'
import { useReactMonthView } from '../useReactMonthView'
import { ConnectedWeekView } from '../WeekView/ConnectedWeekView'
import { ContentDragContextProvider } from '../WeekView/ContentDragContext'
import { MomentDragContextProvider } from '../WeekView/MomentDragContext'
import WeekHeader from '../WeekView/WeekHeader'
import { WeekViewSettingsProvider } from '../WeekView/WeekViewSettingsContext'
import { actionTypes, reducer } from './reducer'
import './styles.scss'

const CalendarComponent = ({
  activeFilters,
  filterValues,
  selectedFilters,
  activeFiltersCount,
  createPresentation,
  currentWeekLabelSets,
  fetchTimeline,
  focusedDay,
  getStory,
  hasUnseenNotification,
  isExportPopoverOpen,
  isFiltersTrayOpen,
  isShroudVisible,
  joinBrand,
  launchMomentDuplicator,
  mode,
  moments,
  navigateTo,
  unfilteredMomentCount,
  onAddToBoard,
  onAddToPlan,
  onActionsMenuSelect,
  onClickCreateMomentButton,
  onDuplicateContent,
  onExportPopoverConfirm,
  onNavigateDayView,
  onShareClick,
  renderDayView,
  renderMonthView,
  requestPrivacyGroupAccess,
  saveViewingMode,
  searchDayViewMoments,
  searchMonthViewMoments,
  setWeekViewStartDay,
  story,
  storyId,
  theme = 'light',
  toggleFilters,
  totalContentInWeek,
  updateContent,
  updateMoment,
  weekStartsOn,
}) => {
  const workspace = useWorkspace()
  const { roleIsNot } = useBackboneUserRole()
  const isReactMonthView = useReactMonthView()
  const { data: features } = useFeatureFlags()
  const [state, dispatch] = useReducer(reducer, {
    mode,
    // A link to the React month carries the keyword its sender searched for.
    searchTerm:
      (mode === Timescale.Month &&
        isReactMonthView &&
        parseQuickfindHash(window.location.hash)) ||
      null,
    firstDayOfWeek: startOfWeek(focusedDay, { weekStartsOn }),
    focusedDay,
  })

  const calendarRef = useRef(true)

  const navigate = useCallback(
    (day) => {
      if (state.mode === Timescale.Month) return

      const firstDayOfWeek = startOfWeek(day, { weekStartsOn })
      dispatch({
        type: actionTypes.CHANGE_DAY,
        payload: { day, firstDayOfWeek },
      })

      onNavigateDayView(day)

      setWeekViewStartDay(firstDayOfWeek)
    },
    [onNavigateDayView, setWeekViewStartDay, state.mode, weekStartsOn]
  )

  // The Day strip lands on the first day at or after the one asked for that
  // carries content. Follow it, so the label names the day on screen. This
  // reports where the view landed; it never asks Backbone to move again.
  const onLandOnDay = useCallback(
    (day) => {
      dispatch({
        type: actionTypes.CHANGE_DAY,
        payload: { day, firstDayOfWeek: startOfWeek(day, { weekStartsOn }) },
      })
    },
    [weekStartsOn]
  )

  // The day view can only show a day that carries content, so previous and
  // next skip to the closest such day. A step to an empty day would move the
  // label while the view stays where it is.
  const histogramData = useRef({})
  const onHistogramDataChange = useCallback((data) => {
    histogramData.current = data
  }, [])
  const stepToDayWithContent = (step) => {
    const day = getAdjacentDateWithData(
      histogramData.current,
      state.focusedDay,
      step
    )
    if (day) navigate(day)
  }

  useEffect(() => {
    if (state.mode === Timescale.Day) {
      renderDayView(navigate)
    }
    // React draws Month itself when the flag is on, so Backbone is never asked
    // to mount into a node that is no longer there.
    if (state.mode === Timescale.Month && !isReactMonthView) {
      renderMonthView()
    }
  }, [isReactMonthView, navigate, renderDayView, renderMonthView, state.mode])

  useEffect(() => {
    if (!calendarRef.current) return
    // A response that names the story as a relationship leaves only its id in
    // the store, so a story with no uuid has not been fetched yet.
    if (storyId && !story?.uuid) {
      getStory()
    }
  }, [getStory, storyId, story])

  // Backbone puts its day back to today when its timescale changes. A day the
  // shell picked on the way is handed over after that, or it would be lost.
  const dayForBackbone = useRef(null)
  useEffect(() => {
    if (calendarRef.current) {
      calendarRef.current = false
      return
    }
    saveViewingMode(state.mode)
    if (dayForBackbone.current) {
      onNavigateDayView(dayForBackbone.current)
      dayForBackbone.current = null
    }
  }, [onNavigateDayView, saveViewingMode, state.mode])

  useEffect(() => {
    if (state.searchTerm === null) return
    if (state.mode === Timescale.Day) {
      searchDayViewMoments(state.searchTerm)
    }
    // The React month has its own quickfind, and the shell's search box is
    // hidden while it is on, so this term can only be stale.
    if (state.mode === Timescale.Month && !isReactMonthView) {
      searchMonthViewMoments(state.searchTerm)
    }
  }, [
    isReactMonthView,
    searchDayViewMoments,
    searchMonthViewMoments,
    state.mode,
    state.searchTerm,
  ])

  const search = useCallback((term) => {
    dispatch({ type: actionTypes.SET_SEARCH_TERM, payload: term })
  }, [])

  // The search box keeps what was typed into it, so it is drawn again to show
  // a keyword the month's address names.
  const [searchBoxKey, setSearchBoxKey] = useState(0)
  const followMonthSearch = useCallback(
    (term) => {
      search(term)
      setSearchBoxKey((key) => key + 1)
    },
    [search]
  )

  // The month grid draws whole weeks, so what it shows reaches into the months
  // either side of the one it is on. It says which days those are.
  const [monthRange, setMonthRange] = useState(null)
  const onMonthRangeChange = useCallback(
    (start, end) => setMonthRange({ end, start }),
    []
  )
  // The grid's first day can belong to the month before. Six days on is always
  // inside the month the grid is for.
  const visibleMonth = useMemo(
    () => monthRange && addDays(monthRange.start, 6),
    [monthRange]
  )

  // The month keeps its filters to itself, so it hands them over for the
  // preview to carry.
  const [monthPreviewParams, setMonthPreviewParams] = useState({})
  const onMonthFilterStateChange = useCallback(
    (filters, trees) =>
      setMonthPreviewParams(monthFilterPreviewParams(filters, trees)),
    []
  )

  // The iframe's hash names the last month the React month drew, which is not
  // the month of the week a person comes from.
  const [monthToOpen, setMonthToOpen] = useState(undefined)

  const setMode = useCallback(
    (newMode) => {
      if (isReactMonthView && newMode === Timescale.Month) {
        setMonthToOpen(startOfMonth(state.focusedDay))
      }

      // Week opens in the month the person was reading: on today's week if
      // the month holds today, and on the week of the 15th if it does not.
      if (
        isReactMonthView &&
        visibleMonth &&
        state.mode === Timescale.Month &&
        newMode === Timescale.Week
      ) {
        const today = startOfDay(new Date())
        const day = isSameMonth(today, visibleMonth)
          ? today
          : setDate(visibleMonth, 15)
        const firstDayOfWeek = startOfWeek(day, { weekStartsOn })

        dispatch({
          type: actionTypes.CHANGE_DAY,
          payload: { day, firstDayOfWeek },
        })
        setWeekViewStartDay(firstDayOfWeek)
        dayForBackbone.current = day
      }

      dispatch({ type: actionTypes.CHANGE_MODE, payload: newMode })
      parent.postMessage(
        { message: 'asgard:route:backbone_execute', route: `#${newMode}` },
        '*'
      )
    },
    [
      isReactMonthView,
      setWeekViewStartDay,
      state.focusedDay,
      state.mode,
      visibleMonth,
      weekStartsOn,
    ]
  )

  const previewRange = useMemo(() => {
    if (state.mode === Timescale.Day) {
      return { end: state.focusedDay, start: state.focusedDay }
    }
    if (state.mode === Timescale.Week) {
      return {
        end: addDays(state.firstDayOfWeek, 6),
        start: state.firstDayOfWeek,
      }
    }
    const today = new Date()
    return monthRange ?? { end: endOfMonth(today), start: startOfMonth(today) }
  }, [monthRange, state.firstDayOfWeek, state.focusedDay, state.mode])

  // Tells Gem which stretch of the calendar a person is looking at. It reads
  // the range on screen rather than the week Backbone last landed on, because
  // paging the React month moves neither Backbone nor the week.
  const sentEntityContext = useRef(null)
  useEffect(() => {
    const dateRange = {
      start_date: previewRange.start,
      end_date: previewRange.end,
      timescale: state.mode,
    }

    // Re-rendering is not moving. The legacy calendar guarded this too: the
    // same range must never be sent twice in a row.
    const sending = `${dateRange.start_date}/${dateRange.end_date}/${dateRange.timescale}`
    if (sending === sentEntityContext.current) return
    sentEntityContext.current = sending

    window.top.postMessage(
      {
        message: MessageKeys.setEntityContext,
        entity_context: {
          entity_type: 'calendar',
          metadata: { date_range: dateRange },
        },
      },
      '*'
    )
  }, [previewRange, state.mode])

  // Backbone never follows the React month, so Present would build the month
  // Backbone last landed on. Hand it the month on screen instead. Every other
  // view leaves the dates to Backbone, exactly as before.
  const presentVisibleView = useCallback(() => {
    if (!isReactMonthView || state.mode !== Timescale.Month) {
      createPresentation()
      return
    }

    const month = visibleMonth ?? new Date()
    createPresentation({ end: endOfMonth(month), start: startOfMonth(month) })
  }, [createPresentation, isReactMonthView, state.mode, visibleMonth])

  const setDroppedMoment = useCallback(
    (momentId, movedTo) => {
      const movedMomentFrom = moments[momentId].scheduled_at
      const movedMomentTo = formatISO(movedTo, { representation: 'date' })
      dispatch({
        type: actionTypes.SET_DROPPED_MOMENT,
        payload: { momentId, movedMomentFrom, movedMomentTo },
      })
    },
    [moments]
  )

  const unsetDroppedMoment = useCallback(() => {
    dispatch({ type: actionTypes.UNSET_DROPPED_MOMENT })
  }, [])

  const onUpdateMoment = useCallback(
    ({ momentId, update }) => {
      const modifiedUpdate = {
        ...update,
        isSaving: true,
      }
      updateMoment({ momentId, update: modifiedUpdate })
      const originalDate = moments[momentId].scheduled_at
      const momentHasMoved = !isSameDay(originalDate, update.scheduled_at)

      if (momentHasMoved) {
        setDroppedMoment(momentId, update.scheduled_at)
      }
    },
    [moments, setDroppedMoment, updateMoment]
  )

  // Histogram-side content reschedule — mirrors WeekView's grid-level
  // `onRescheduleContent`. `PostUpdater` converts takedown to an interval.
  const onRescheduleContent = useCallback(
    ({ contentId, scheduled_at, takedown_scheduled_at }) => {
      const params = { scheduled_at }
      if (takedown_scheduled_at) {
        params.takedown_scheduled_at = takedown_scheduled_at
      }
      updateContent({ contentId, params, updateReduxEntity: true })
    },
    [updateContent]
  )

  const onOpenMonthItem = useCallback(
    (item, { returnTo } = {}) => {
      // The composer opens as a layer over this same page and hands an address
      // to the parent frame as it closes. `returnTo` is the address both
      // shrouds look for, so naming the month here is what brings a person
      // back to the month — and the filters — they left.
      const navigateBack = (params) =>
        navigateTo(
          returnTo
            ? {
                ...params,
                route: `${params.route}?returnTo=${encodeURIComponent(returnTo)}`,
              }
            : params
        )

      // These routes take the v2 primary key — `/moments/:slug/:momentId(\d+)`
      // — so the uuid the month is keyed by names no page at all.
      if (item.kind === 'moment') {
        if (!item.legacyId) return
        navigateToMoment({
          momentId: item.legacyId,
          navigateTo: navigateBack,
          storyId,
        })
        return
      }
      // A link to content names its moment as well. Either id missing means
      // there is no page to send anyone to, and half a route sends them to a
      // 404 instead of leaving them where they are.
      if (!item.legacyId || !item.parentLegacyId) return
      navigateToContent({
        contentId: item.legacyId,
        momentId: item.parentLegacyId,
        navigateTo: navigateBack,
        storyId,
      })
    },
    [navigateTo, storyId]
  )

  // The three actions the month view cannot carry out itself. Each opens one of
  // the page's own modals, which work on the moment's uuid — or, for the
  // duplicator, on the Backbone model it looks up by legacy id. An action this
  // workspace may not use is left out, and the month leaves it off the menu.
  const monthHostActions = useMemo(
    () => ({
      onAddToBoard:
        features?.boards_view && onAddToBoard
          ? (item, { shouldUnschedule }) =>
              onAddToBoard({
                moment: { uuid: item.id },
                defualtUnschedule: shouldUnschedule,
              })
          : undefined,
      onAddToPlan:
        features?.campaign_planner && onAddToPlan
          ? (item) => onAddToPlan({ moment: { uuid: item.id } })
          : undefined,
      onDuplicateMoment: launchMomentDuplicator
        ? (item) => {
            // The duplicator looks the moment up by legacy id; without one
            // there is nothing to hand it.
            if (item.legacyId) launchMomentDuplicator(item.legacyId)
          }
        : undefined,
      onPreviewRange: (start, end) =>
        parent.postMessage(
          {
            message: MessageKeys.execute,
            replace: false,
            route: inMarketPreviewRoute(
              workspace.slug,
              start,
              end,
              monthPreviewParams
            ),
          },
          '*'
        ),
    }),
    [
      features?.boards_view,
      features?.campaign_planner,
      launchMomentDuplicator,
      monthPreviewParams,
      onAddToBoard,
      onAddToPlan,
      workspace.slug,
    ]
  )

  // Inside a story, only the days the story runs for can hold anything new.
  const storyDateRange = useMemo(
    () =>
      story
        ? { end: story.scheduled_end_at, start: story.scheduled_start_at }
        : undefined,
    [story]
  )

  const onUndoDropOnHistogram = useCallback(() => {
    const momentId = state.droppedMoment.id.toString()
    unsetDroppedMoment()

    const update = {
      isSaving: true,
      scheduled_at: formatISO(state.droppedMoment.movedFrom, {
        representation: 'date',
      }),
    }
    updateMoment({ momentId, update })
  }, [state.droppedMoment, unsetDroppedMoment, updateMoment])

  return (
    // The monolith page mounts no gem colour variables of its own, and every
    // themed variable is declared against `data-gem-theme`. Without both of
    // these the header's colours and rules resolve to nothing at all.
    <main className={kuiperClassNames('calendar')} data-gem-theme={theme}>
      <GlobalGemColors />
      <CurrentUserProvider>
        {/*
         * WeekViewSettingsProvider wraps the Header so that the Header can
         * read `viewMode` and hide the "Create a Moment" button when
         * Content mode is active. Unused settings when not in Week mode
         * are harmless (they just sit in localStorage).
         */}
        <WeekViewSettingsProvider key={workspace?.uuid ?? 'default'}>
          <Header
            key={searchBoxKey}
            createPresentation={presentVisibleView}
            initialSearchTerm={state.searchTerm ?? undefined}
            mode={state.mode}
            hasUnseenNotification={hasUnseenNotification}
            isExportPopoverOpen={isExportPopoverOpen}
            onActionsMenuSelect={onActionsMenuSelect}
            onClickCreateMomentButton={onClickCreateMomentButton}
            onExportPopoverConfirm={onExportPopoverConfirm}
            onShareClick={onShareClick}
            onModeChange={setMode}
            onSearch={search}
            previewFilterParams={
              isReactMonthView && state.mode === Timescale.Month
                ? monthPreviewParams
                : undefined
            }
            previewRange={previewRange}
            {...{
              theme,
            }}
          />

          <Conditional condition={state.mode === Timescale.Week}>
            {() => (
              <WeekHeader
                activeFiltersCount={activeFiltersCount}
                focusedDay={state.firstDayOfWeek}
                isFiltersTrayOpen={isFiltersTrayOpen}
                onToggleFilters={toggleFilters}
                onWeekChange={navigate}
                story={story}
                weekStartsOn={weekStartsOn}
                {...{
                  theme,
                }}
              />
            )}
          </Conditional>

          {/*
           * The legacy month draws its own date nav, so its control bar only
           * carries the filter button the shared header no longer shows.
           */}
          <Conditional
            condition={state.mode === Timescale.Month && !isReactMonthView}
          >
            {() => (
              <ControlBar
                start={
                  <FilterButton
                    activeFiltersCount={activeFiltersCount}
                    isFiltersTrayOpen={isFiltersTrayOpen}
                    onToggleFilters={toggleFilters}
                  />
                }
              />
            )}
          </Conditional>

          <Conditional condition={state.mode === Timescale.Day}>
            {() => (
              <ControlBar
                start={
                  <FilterButton
                    activeFiltersCount={activeFiltersCount}
                    isFiltersTrayOpen={isFiltersTrayOpen}
                    onToggleFilters={toggleFilters}
                  />
                }
                center={
                  <DateNavigator
                    label={format(state.focusedDay, 'EEEE, MMMM d')}
                    nextLabel="Go to next day"
                    onDateSelect={navigate}
                    onNext={() => stepToDayWithContent(1)}
                    onPrevious={() => stepToDayWithContent(-1)}
                    onToday={() => navigate(new Date())}
                    previousLabel="Go to previous day"
                    selectedDate={state.focusedDay}
                    weekStartsOn={weekStartsOn}
                  />
                }
              />
            )}
          </Conditional>

          <Conditional condition={state.mode !== Timescale.Month}>
            {() => (
              <ConnectedHistogram
                contentCountForCurrentWeek={totalContentInWeek}
                fetchHistogramData={fetchTimeline}
                focusedDay={
                  state.mode === Timescale.Day
                    ? state.focusedDay
                    : state.firstDayOfWeek
                }
                mode={state.mode}
                momentCountForCurrentWeek={unfilteredMomentCount}
                onFocusedDayChange={onLandOnDay}
                onHistogramDataChange={onHistogramDataChange}
                onNavigation={navigate}
                onRescheduleContent={onRescheduleContent}
                onUpdateMoment={onUpdateMoment}
                searchTerm={state.searchTerm}
                story={story}
                weekStartsOn={weekStartsOn}
                {...{
                  theme,
                }}
              />
            )}
          </Conditional>

          <Conditional condition={state.mode === Timescale.Day}>
            <DayView />
          </Conditional>

          <Conditional condition={state.mode === Timescale.Week}>
            <Fragment>
              <MomentDropToast
                dropData={state.droppedMoment}
                onClose={unsetDroppedMoment}
                onUndo={onUndoDropOnHistogram}
              />

              <AnalyticsConsumer>
                {(tracker) => (
                  <ConnectedWeekView
                    activeFilters={activeFilters}
                    selectedFilters={selectedFilters}
                    brandId={state.current_brand_id}
                    currentWeekLabelSets={currentWeekLabelSets}
                    focusedDay={state.firstDayOfWeek}
                    joinBrand={joinBrand}
                    launchMomentDuplicator={launchMomentDuplicator}
                    onAddToBoard={onAddToBoard}
                    onAddToPlan={onAddToPlan}
                    onDayChange={navigate}
                    onDuplicateContent={onDuplicateContent}
                    requestPrivacyGroupAccess={requestPrivacyGroupAccess}
                    searchTerm={state.searchTerm}
                    story={story}
                    storyId={storyId}
                    totalContentInWeek={totalContentInWeek}
                    trackers={tracker}
                    weekStartsOn={weekStartsOn}
                    {...{
                      theme,
                    }}
                  />
                )}
              </AnalyticsConsumer>
            </Fragment>
          </Conditional>

          {/* The empty node Backbone mounts the Marionette month view into. */}
          <Conditional
            condition={state.mode === Timescale.Month && !isReactMonthView}
          >
            <div className={kuiperClassNames('month-view')} />
          </Conditional>

          {/*
           * On a story's own page the calendar waits for the story itself. The
           * month endpoint names a story by uuid, while `storyId` here is the
           * legacy numeric id every other view uses, so drawing early would
           * either be rejected or — worse — show the whole workspace inside one
           * story.
           */}
          <Conditional
            condition={
              state.mode === Timescale.Month &&
              isReactMonthView &&
              (!storyId || Boolean(story?.uuid))
            }
          >
            {() => (
              <ThemeProvider className={kuiperClassNames('month-container')}>
                {/*
                 * Lapidary portals its popovers into theme containers at the
                 * end of <body>, far below the legacy shell's z-index 100
                 * pinned section. Lift them while the month view owns the
                 * page; the container stays click-transparent so only the
                 * portaled content itself takes pointer events.
                 *
                 * The lift has to clear the legacy content editor too: it
                 * opens over this page at z-index 1001 and its own date
                 * pickers portal into the same container, so a lower value
                 * hides them behind the editor's sidebar.
                 */}
                <Global
                  styles={{
                    'body > .gem-theme-container': {
                      pointerEvents: 'none',
                      position: 'relative',
                      zIndex: 1100,
                    },
                    'body > .gem-theme-container > *': {
                      pointerEvents: 'auto',
                    },
                  }}
                />
                <WorkspaceMonthCalendar
                  /*
                   * The month knows nothing about roles. These are the two
                   * rules the header above it keeps: a view-only reader
                   * changes nothing, and a limited one makes nothing new.
                   */
                  canCreate={roleIsNot('limited', 'view_only')}
                  canEdit={roleIsNot('view_only')}
                  hostActions={monthHostActions}
                  initialMonth={monthToOpen}
                  /*
                   * The composer is a layer over this same page, so the window
                   * never loses focus while it is open. This is how the month
                   * hears that a person has come back to it.
                   */
                  isComposerOpen={isShroudVisible}
                  onFilterStateChange={onMonthFilterStateChange}
                  onNavigate={onOpenMonthItem}
                  onQuickfindChange={followMonthSearch}
                  onVisibleRangeChange={onMonthRangeChange}
                  quickfind={state.searchTerm ?? ''}
                  storyDateRange={storyDateRange}
                  storyId={story?.uuid}
                  /*
                   * The month endpoint names a story by uuid; its page is
                   * addressed by the legacy id, and that is the one the
                   * address a person comes back to has to carry.
                   */
                  storyLegacyId={storyId}
                  /*
                   * A month with nothing filtered of its own starts from what
                   * the tray is filtering the other views by. It is a starting
                   * point, not a link: the month never writes back here.
                   */
                  weekFilterDates={filterValues}
                  weekFilters={activeFilters}
                  weekStartsOn={weekStartsOn}
                />
              </ThemeProvider>
            )}
          </Conditional>
        </WeekViewSettingsProvider>
      </CurrentUserProvider>
    </main>
  )
}

CalendarComponent.propTypes = {
  activeFilters: PropTypes.array,
  filterValues: PropTypes.object,
  selectedFilters: PropTypes.object,
  activeFiltersCount: PropTypes.number,
  currentWeekLabelSets: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  fetchTimeline: PropTypes.func.isRequired,
  focusedDay: PropTypes.instanceOf(Date).isRequired,
  hasUnseenNotification: PropTypes.bool,
  getStory: PropTypes.func.isRequired,
  isExportPopoverOpen: PropTypes.bool,
  isFiltersTrayOpen: PropTypes.bool,
  isShroudVisible: PropTypes.bool,
  joinBrand: PropTypes.func,
  launchMomentDuplicator: PropTypes.func,
  mode: oneOf([Timescale.Day, Timescale.Month, Timescale.Week]).isRequired,
  moments: PropTypes.object,
  navigateTo: PropTypes.func,
  unfilteredMomentCount: PropTypes.number,
  onActionsMenuSelect: PropTypes.func,
  onAddToBoard: PropTypes.func,
  onAddToPlan: PropTypes.func,
  onExportPopoverConfirm: PropTypes.func,
  onClickCreateMomentButton: PropTypes.func,
  onDuplicateContent: PropTypes.func.isRequired,
  onShareClick: PropTypes.func,
  onNavigateDayView: PropTypes.func.isRequired,
  renderDayView: PropTypes.func.isRequired,
  renderMonthView: PropTypes.func.isRequired,
  requestPrivacyGroupAccess: PropTypes.func.isRequired,
  saveViewingMode: PropTypes.func.isRequired,
  searchDayViewMoments: PropTypes.func.isRequired,
  searchMonthViewMoments: PropTypes.func.isRequired,
  setWeekViewStartDay: PropTypes.func.isRequired,
  story: PropTypes.object,
  storyId: PropTypes.string,
  theme: PropTypes.oneOf(['dark', 'light']),
  toggleFilters: PropTypes.func.isRequired,
  totalContentInWeek: PropTypes.number,
  updateContent: PropTypes.func.isRequired,
  updateMoment: PropTypes.func.isRequired,
  weekStartsOn: PropTypes.number,
}

const Calendar = (props) => {
  return (
    <DefaultQueryClientProvider>
      <MomentDragContextProvider>
        <ContentDragContextProvider>
          <CalendarComponent {...props} />
        </ContentDragContextProvider>
      </MomentDragContextProvider>
    </DefaultQueryClientProvider>
  )
}

Calendar.propTypes = CalendarComponent.propTypes

export { Calendar }
export default Calendar
